import * as yup from 'yup'
import { MakeRequiredAndNullable } from './utils.js'

export enum NoodlesOrderLineItemKeys {
  itemNumber = 'itemNumber', // line item number, not to be entered by user
  itemName = 'itemName', // name of the item, often SKU, Unique ID, refernce ID, etc
  description = 'description', // verbose description of the item (human parseable)
  unitPrice = 'unitPrice', // price per unit
  itemQty = 'itemQty', // quantity of the item for order
  itemNote = 'itemNote', // note for the item
  itemDiscountRate = 'itemDiscountRate', // discount rate for the item
  lineTotal = 'lineTotal', // total cost per (unitPrice * itemQty), not to be entered by user
  itemWeight = 'itemWeight', // weight of the item
}

export enum NoodlesOrderPaymentKeys {
  paymentNumber = 'paymentNumber', // payment number, not to be entered by user
  transactionId = 'transactionId', // transaction ID
  date = 'date', // date of the transaction
  status = 'status', // status of the transaction
  amount = 'amount', // amount of the transaction
  balance = 'balance', // balance of the transaction
}

export const noodlesAddressSchema = yup.object().shape({
  companyName: yup.string().nullable().optional(),
  addressLine1: yup.string().nullable().optional(),
  addressLine2: yup.string().nullable().optional(),
  postalCode: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
})
export type NoodlesAddress = yup.InferType<typeof noodlesAddressSchema>

export const noodlesRemittanceInstructionsSchema = yup.object().shape({
  bankName: yup.string().nullable().optional(),
  bankAddress: noodlesAddressSchema.nullable().optional(),
  kto: yup.string().nullable().optional(),
  blz: yup.string().nullable().optional(),
  iban: yup.string().nullable().optional(),
  swift: yup.string().nullable().optional(),
  firstName: yup.string().nullable().optional(),
  lastName: yup.string().nullable().optional(),
  ustIdNr: yup.string().nullable().optional(),
  beneficiaryName: yup.string().nullable().optional(),
  beneficiaryAddress: noodlesAddressSchema.nullable().optional(),
  telephone: yup.string().nullable().optional(),
  telefax: yup.string().nullable().optional(),
  email: yup.string().nullable().optional(),
  website: yup.string().nullable().optional(),
  paymentLink: yup.string().nullable().optional(),
})
export type NoodlesRemittanceInstructions = yup.InferType<
  typeof noodlesRemittanceInstructionsSchema
>

export const noodlesOrderFileDataSchema = yup.object().shape({
  issuer: yup.object().shape({
    companyName: yup.string().nullable().optional(),
    address: noodlesAddressSchema.nullable().optional(),
    firstName: yup.string().nullable().optional(),
    lastName: yup.string().nullable().optional(),
    phoneNumber: yup.string().nullable().optional(),
    emailAddress: yup.string().nullable().optional(),
  }),
  orderNumber: yup.string().nullable().optional(),
  referenceNumber: yup.string().nullable().optional(),
  customerNumber: yup.string().nullable().optional(),
  dateIssued: yup.string().nullable().optional(),
  dateReady: yup.string().nullable().optional(),
  paymentTerms: yup.string().nullable().optional(),
  shippingMethod: yup.string().nullable().optional(),
  weight: yup.string().nullable().optional(),
  vatId: yup.string().nullable().optional(),
  externalNote: yup.string().nullable().optional(),
  customer: yup.object().shape({
    billingDetails: yup
      .object()
      .shape({
        companyName: yup.string().nullable().optional(),
        billingAddress: noodlesAddressSchema.nullable().optional(),
        firstName: yup.string().nullable().optional(),
        lastName: yup.string().nullable().optional(),
        phoneNumber: yup.string().nullable().optional(),
        emailAddress: yup.string().nullable().optional(),
      })
      .nullable()
      .optional(),
    shippingDetails: yup
      .object()
      .shape({
        companyName: yup.string().nullable().optional(),
        shippingAddress: noodlesAddressSchema.nullable().optional(),
        firstName: yup.string().nullable().optional(),
        lastName: yup.string().nullable().optional(),
        phoneNumber: yup.string().nullable().optional(),
        emailAddress: yup.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  }),
  items: yup
    .array()
    .of(
      yup.object().shape({
        itemNumber: yup.number().nullable().optional(),
        itemName: yup.string().nullable().optional(),
        description: yup.string().nullable().optional(),
        itemQty: yup.string().nullable().optional(),
        unitPrice: yup.string().nullable().optional(),
        itemNote: yup.string().nullable().optional(),
        itemDiscountRate: yup.string().nullable().optional(),
        lineTotal: yup.string().nullable().optional(),
        itemWeight: yup.string().nullable().optional(),
      }),
    )
    .required(),
  taxRate: yup.string().nullable().optional(),
  shippingFee: yup.string().nullable().optional(),
  otherFees: yup.string().nullable().optional(),
  agreement: yup.string().nullable().optional(),
  instructions: yup.string().nullable().optional(),
  remittanceInstructions: noodlesRemittanceInstructionsSchema,
  isNet: yup.string().nullable().optional(),
  showPaymentBalance: yup.string().nullable().optional(),
  showPaymentLink: yup.string().nullable().optional(),
  showBankDetails: yup.string().nullable().optional(),
  payments: yup
    .array()
    .of(
      yup.object().shape({
        paymentNumber: yup.number().nullable().optional(),
        transactionId: yup.string().nullable().optional(),
        date: yup.string().nullable().optional(),
        status: yup.string().nullable().optional(),
        amount: yup.string().nullable().optional(),
        balance: yup.string().nullable().optional(),
      }),
    )
    .required(),
  paymentBalance: yup.string().nullable().optional(),
})

export type NoodlesOrderFileData = yup.InferType<typeof noodlesOrderFileDataSchema>
type RequiredFileData = MakeRequiredAndNullable<NoodlesOrderFileData>

export const noodlesOrderFileMappings: RequiredFileData = {
  issuer: {
    companyName: '1347f256-37d3-486a-9209-07fa3d4f05a3',
    address: {
      addressLine1: 'a0c2474f-4142-4962-ad43-a229320b307d',
      addressLine2: '0a8446bb-8623-407e-9a2d-feeaf2d55817',
      postalCode: '9d39ead4-b9b3-451a-badd-33cc350890ba',
      city: '5996d0a2-c15c-4ba8-8d56-5836e702b4f4',
      country: '23380b5e-2a55-439b-9b48-728491a884d3',
    },
    firstName: 'a9120122-8058-4ecf-9a98-e26855c1a2c8',
    lastName: '19e4101c-9889-4fa2-bea0-9b6139beb6c9',
    phoneNumber: 'd178a6e9-dd51-4b44-a16a-361b15c686f4',
    emailAddress: '1038b08d-351d-4636-9b2a-8a80d323aaf6',
  },
  orderNumber: 'e7bf4b74-af44-45b7-b1c5-7b097616a6fb',
  referenceNumber: '0893daa1-babc-4d60-82a0-75034a371e5f',
  customerNumber: '482ae2b9-e367-4c53-8e86-d4e1d740f9af',
  paymentTerms: 'cc48bcad-fc2c-4b1f-8f1a-bead3a271279',
  dateIssued: '856ff818-efd4-4171-8f8b-10edda035113',
  dateReady: '9bd9ef55-2a0a-4bad-8f9a-199b28e7d627',
  shippingMethod: 'd5f517f2-6c9e-4326-878e-555031448be8',
  weight: '777c3028-4d54-40bd-822f-5805a2f7da19',
  vatId: 'ff08eb94-f493-4283-bf5f-eec125996ec1',
  externalNote: 'b0d730a4-85d6-4899-9924-4420b959833c',
  customer: {
    billingDetails: {
      firstName: '8a6f876f-4e87-4d55-a9ce-bbc3c0cf97ea',
      lastName: '34884bf1-eff8-41b9-b4b6-11cab0af2399',
      companyName: '498e7ad1-8d22-4c89-a9f7-68123ca0bb35',
      billingAddress: {
        addressLine1: '1709aed7-9c61-4ed3-8355-28098794d117',
        addressLine2: '820edf85-bfe0-474e-9cd9-b48e387c8f28',
        postalCode: 'fcb41126-89f6-4b53-a640-b94ab5569eec',
        city: '9341b9e3-0a6c-4bdb-a98b-0c8bea73cca5',
        country: '85080ff4-e142-4dc5-ab83-292c3d143f7f',
      },
      phoneNumber: 'e641b797-dd4c-4973-ad7d-3f413a7526a9',
      emailAddress: 'a5da297c-7e2c-41bd-9457-99fd5f894742',
    },
    shippingDetails: {
      firstName: 'cd052e4b-aafe-4285-868d-8a0315537a2d',
      lastName: 'e867ef42-35ab-48fe-81ce-2e53f86e9167',
      companyName: '19b2e68c-999e-49c1-93e1-9f545f4059f2',
      shippingAddress: {
        addressLine1: '07f24343-0043-48fa-8d35-83c46150b102',
        addressLine2: 'ffc6349e-be53-4fc8-b452-5e8a0c8fa997',
        postalCode: 'e68216c8-d12c-48c2-a834-7f8884ffe63a',
        city: '003f98b4-6d2c-495c-bd32-1da57a937252',
        country: '09f8ca86-3e9a-4b4d-b831-faab1c0dfad6',
      },
      phoneNumber: 'daa01fb6-3c0a-428d-9ffb-6cf4b810b203',
      emailAddress: 'e1c10915-1276-40e7-805a-751ec7e85ec7',
    },
  },
  items: [
    {
      itemNumber: null,
      itemName: '9c387baf-b83a-479b-b824-6295380f27d8',
      description: '05875f2b-c05e-4170-9b4c-8b943384123a',
      itemQty: '9810b7ff-4add-4a76-9786-de527cfcffc8',
      itemNote: 'cf9bd5be-79be-42ca-844e-e1b2280bc6d9',
      itemDiscountRate: '568708a5-99e5-4423-9816-437d11f5aa71',
      unitPrice: 'fb49f354-d094-4868-acb6-6501547684f0',
      lineTotal: null,
      itemWeight: '5f5aa82a-77a2-4225-896b-bd7f6e1e36f4',
    },
  ],
  taxRate: 'de214741-3adf-459f-9941-26481cdf2ae0',
  shippingFee: '770e3082-f692-4d79-ae64-eddcaa2a4617',
  otherFees: '20c63b37-228b-40ec-9402-c0ba78f2b0d6',
  agreement: '42a6c4ef-ef8d-4eac-a531-e7361958e159',
  instructions: '2b18717a-0679-4c19-b2bd-e3d73d2f30ad',
  remittanceInstructions: {
    bankName: '7ed71d73-c620-47f0-99fe-ac4abed19a26',
    bankAddress: {
      addressLine1: '96793377-cda7-4133-88d7-ab6cbfef1dbf',
      addressLine2: '20066543-286e-4e36-985f-3743ba67b8eb',
      postalCode: 'a9f73c54-c9d7-4007-92d0-e9d2996cf437',
      city: 'cefb970c-3232-42f2-b2b3-e17c4875132c',
      country: '7250166a-5638-45e8-95ff-89c36b9e942f',
    },
    kto: '897c804d-1775-439e-a99c-dca43fe397cd',
    blz: 'e84b54d2-e009-4e62-96a3-ac8166d921c3',
    iban: '1893d744-8e5e-4541-b9d5-c8154ff818bc',
    swift: '27bf5bff-0691-4f13-93a6-0cd0f2c4e0af',
    firstName: '94bbe009-915a-4dbd-8a09-9a08a4fc58df',
    lastName: '63b52c55-698d-40b8-91ab-c19a647d1a5d',
    ustIdNr: 'b70c6a37-0583-400d-8cbd-59bb9d546cce',
    beneficiaryName: '1ad4fa37-ad25-4ea1-9180-2f2f0a1bac90',
    beneficiaryAddress: {
      addressLine1: 'a20b8f5b-bef8-4fd1-800c-784e25006f7a',
      addressLine2: 'f5c56c92-7c95-4d91-9dbf-5474496a6cd3',
      postalCode: '131ac96e-bfd4-4dd0-b52c-2dfc5d98f2d5',
      city: 'e96462ca-7d1d-46dd-a15d-39fa7d3364d3',
      country: 'aa170e0e-1015-4c07-958f-8ca1f256a5f7',
    },
    telephone: 'cfcdfa51-2d5d-428a-87eb-0061273ca19a',
    telefax: '2a723847-8e8d-46d3-b861-5e49dd41a7a5',
    email: 'cb44d22a-782b-4235-8b9b-29c429da324a',
    website: 'dca7cc43-0cef-4bb4-ad7d-16fe828ccfa3',
    paymentLink: '9a0cae61-8fbe-424d-9183-e35219dfac42',
  },
  isNet: 'd589e351-8748-46e1-9081-0d66e266d0fb',
  showPaymentBalance: '9a604fde-3908-41d6-9b4b-2fe2c5263747',
  showPaymentLink: 'b796c1af-36ad-420e-b7e0-2045848b2b83',
  showBankDetails: '511d8ed1-2972-4f88-b10a-75eb893080fb',
  payments: [
    {
      paymentNumber: null,
      transactionId: '2e8334d2-bc87-4731-8f46-68b18b24dce0',
      date: '156e289b-f514-4bde-b64d-c88002458620',
      status: 'f904a57b-c8de-4bd7-b9fd-7ff6fca01038',
      amount: '44f11c22-1fb7-4f26-8c85-528600e23d48',
      balance: 'a1c0d2aa-955a-4194-b6cd-b3b088b0bacd',
    },
  ],
  paymentBalance: '097313ad-503c-40bc-88ba-aec82c6468db',
}
